import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Box,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { Close as CloseIcon, NavigateNext, NavigateBefore, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomButton from './CustomButton';

interface WizardStep {
  label: string;
  content: React.ReactNode;
  isValid?: boolean;
}

interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onFinish: () => void;
  title: string;
  steps: WizardStep[];
  loading?: boolean;
  finishLabel?: string;
}

export default function OnboardingWizard({
  open,
  onClose,
  onFinish,
  title,
  steps,
  loading = false,
  finishLabel,
}: OnboardingWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onFinish();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const currentStep = steps[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          minHeight: '60vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {!loading && (
          <Tooltip title={t('common.close')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<CloseIcon fontSize="small" />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={onClose}
              />
            </span>
          </Tooltip>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Stepper Section */}
        <Box
          sx={{
            px: 4,
            py: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              '& .MuiStepLabel-label': {
                mt: 1,
                fontSize: '0.875rem',
              },
            }}
          >
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>{t(step.label)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            flex: 1,
            px: 4,
            py: 3,
            overflowY: 'auto',
          }}
        >
          {currentStep.content}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1,
          justifyContent: 'center',
        }}
      >
        <CustomButton onClick={onClose} variant="secondary" disabled={loading}>
          {t('common.cancel')}
        </CustomButton>

        <CustomButton
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
          icon={<NavigateBefore />}
          iconPosition="left"
          variant="secondary"
        >
          {t('common.back')}
        </CustomButton>

        <CustomButton
          variant="primary"
          onClick={handleNext}
          disabled={currentStep.isValid === false || loading}
        >
          {isLastStep ? finishLabel || t('common.finish') : t('common.next')}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
